import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  cloneVirtualAssistant, 
  exportVirtualAssistant,
  mergeVirtualAssistant,
  updateCoOwner
} from '../../services/api/virtualAssistant';
import { Assistant as SharedAssistant, User } from "../../types/assistant";

export const useSharedAssistantHandlers = (
  fetchSharedAssistantsData: (page: number, pageSize: number, searchTerm: string) => Promise<void>,
  currentPage: number,
  pageSize: number,
  searchTerm: string,
  inference_url: string
) => {
  const navigate = useNavigate();

  const [customModal, setCustomModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "confirmation",
  });

  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  const [deleteAfterMerge, setDeleteAfterMerge] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<SharedAssistant | null>(null);

  const [isCoOwnerModalOpen, setIsCoOwnerModalOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [selectedAppOwner, setSelectedAppOwner] = useState<string>('');
  const [selectedCoOwners, setSelectedCoOwners] = useState<User[]>([]);

  const showCustomModal = (
    title: string, 
    message: string, 
    type: "success" | "error" | "loading" | "confirmation",
    onConfirm?: () => void
  ) => {
    setCustomModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm,
    });
  };

  const showConfirmationModal = (title: string, message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title,
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  const handleAssistantView = (assistant: SharedAssistant) => {
    navigate('/buildNewAssistant', { state: { assistant } });
  };

  const handleClone = async (assistantId: string, assistantName: string, setShowOptions: any) => {
    setShowOptions((prev: { [key: string]: boolean }) => ({
      ...prev,
      [assistantId]: false,
    }));
    
    showConfirmationModal(
      "Clone Confirmation", 
      `Do you want to create a copy of ${assistantName}?`, 
      async () => {
        setCustomModal((prev: any) => ({ ...prev, isOpen: false }));

        setCustomModal({
          isOpen: true,
          title: "Cloning...",
          message: "Please wait while the chatbot is being cloned.",
          type: "loading",
        });

        try {
          const clonedAssistant = await cloneVirtualAssistant(assistantId);
          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: "Chatbot cloned successfully.",
            type: "success",
          });
          navigate('/buildNewAssistant', { state: { assistant: clonedAssistant } });
        } catch (error) {
          console.error('Error cloning chatbot:', error);
          setCustomModal({
            isOpen: true,
            title: "Fail!",
            message: "Failed to clone chatbot.",
            type: "error",
          });
        }
      }
    );
  };

  const handleExport = async (assistant: SharedAssistant, setShowOptions: any) => {
    setShowOptions((prev: { [key: string]: boolean }) => ({
      ...prev,
      [assistant.app_id]: false,
    }));
    
    showConfirmationModal(
      "Export Confirmation", 
      "Do you want to export this chatbot?", 
      async () => {
        setCustomModal((prev: any) => ({
          ...prev,
          isOpen: false,
        }));

        setCustomModal({
          isOpen: true,
          title: "Exporting...",
          message: "Please wait while the chatbot is being exported.",
          type: "loading",
        });

        try {
          const response = await exportVirtualAssistant(assistant.app_id);
          const url = window.URL.createObjectURL(new Blob([response]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${assistant.app_name}.json`);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: "Chatbot exported successfully.",
            type: "success",
          });
        } catch (error) {
          console.error('Error exporting chatbot:', error);
          setCustomModal({
            isOpen: true,
            title: "Error!",
            message: "There was an issue exporting the chatbot.",
            type: "error",
          });
        }
      }
    );
  };

  const handleCopyAppUrl = async (assistantId: string, setShowOptions: any) => {
    setShowOptions((prev: { [key: string]: boolean }) => ({
      ...prev,
      [assistantId]: false,
    }));
    
    const copyingInferenceUrl = inference_url + assistantId;
    await navigator.clipboard.writeText(copyingInferenceUrl);
    setCustomModal({
      isOpen: true,
      title: "Success!",
      message: "Inference URL has been copied successfully.",
      type: "success",
    });
  };

  const handleMergeClick = (assistant: SharedAssistant) => {
    setMergeTarget(null);
    setDeleteAfterMerge(false);
    setSelectedAssistant(assistant);
    setShowMergeModal(true);
  };

  const handleMerge = async (assistantId: string) => {
    if (!mergeTarget) {
      setCustomModal({
        isOpen: true,
        title: "No Target Selected",
        message: "Please select a chatbot to merge with.",
        type: "error",
      });
      return;
    }

    setShowMergeModal(false);
    showCustomModal("Merging...", "Please wait while the chatbots are being merged.", "loading");

    try {
      await mergeVirtualAssistant(assistantId, mergeTarget, deleteAfterMerge);
      showCustomModal("Success!", "Chatbots merged successfully.", "success");
      fetchSharedAssistantsData(currentPage, pageSize, searchTerm);
    } catch (error) {
      console.error('Error merging chatbots:', error);
      showCustomModal("Fail!", "Failed to merge chatbots.", "error");
    }
  };

  const handleCoOwner = async (assistant: SharedAssistant) => {
    setIsCoOwnerModalOpen(true);
    setSelectedAppId(assistant?.app_id);
    setSelectedAppOwner(assistant?.app_owner);
    const coOwners = Array.isArray(assistant?.co_owner) ? assistant.co_owner : [];
    setSelectedCoOwners(coOwners as User[]);
  };

  const handleSaveCoOwners = async (selectedUsers: any, assistantId: any) => {
    try {
      setCustomModal({
        isOpen: true,
        title: "Adding...",
        message: "Please wait while the users are being added.",
        type: "loading",
      });

      const response = await updateCoOwner(assistantId, selectedUsers);

      if (response.detail) {
        setCustomModal({
          isOpen: true,
          title: "Error",
          message: 'Error while adding Users',
          type: "error",
        });
      } else {
        setCustomModal({
          isOpen: true,
          title: "Success!",
          message: "Users have been added successfully.",
          type: "success",
        });
        fetchSharedAssistantsData(currentPage, pageSize, searchTerm);
      }
    } catch (error: any) {
      let errorMessage = "An error occurred while adding users.";

      if (error?.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const details = error.response.data.detail.map((d: any) => d.msg).join(", ");
        errorMessage = `Failed to add users: ${details}`;
      }

      setCustomModal({
        isOpen: true,
        title: "Error",
        message: errorMessage,
        type: "error",
      });
    }
  };

  return {
    // Modal states
    customModal,
    setCustomModal,
    showMergeModal,
    setShowMergeModal,
    mergeTarget,
    setMergeTarget,
    deleteAfterMerge,
    setDeleteAfterMerge,
    selectedAssistant,
    isCoOwnerModalOpen,
    setIsCoOwnerModalOpen,
    selectedAppId,
    selectedAppOwner,
    selectedCoOwners,

    // Handler functions
    handleAssistantView,
    handleClone,
    handleExport,
    handleCopyAppUrl,
    handleMergeClick,
    handleMerge,
    handleCoOwner,
    handleSaveCoOwners,

    // Utility functions
    showCustomModal,
    showConfirmationModal,
  };
};